import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface AIInsight {
  id: string;
  mandate: string;
  type: 'pattern' | 'accuracy' | 'efficiency';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  icon: string;
}

const insights: AIInsight[] = [
  {
    id: "1",
    mandate: "Innovate Zurich AG",
    type: 'pattern',
    title: "New Invoice Pattern Detected",
    description: "AI identified recurring supplier 'TechComponents AG' with 98% confidence",
    impact: 'high',
    icon: "fa-solid fa-lightbulb"
  },
  {
    id: "2",
    mandate: "Alpine Logistics GmbH",
    type: 'accuracy',
    title: "VAT Classification Improved",
    description: "Model accuracy increased by 3.2% for transport receipts",
    impact: 'medium',
    icon: "fa-solid fa-chart-line"
  },
  {
    id: "3",
    mandate: "Swiss Precision SA",
    type: 'efficiency',
    title: "Processing Speed Optimized",
    description: "15% faster processing for manufacturing invoices",
    impact: 'medium',
    icon: "fa-solid fa-bolt"
  }
];

export const MandateAIInsights = () => {
  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'pattern': return 'text-purple-600';
      case 'accuracy': return 'text-green-600';
      case 'efficiency': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-bold text-brand-primary">AI Learning Insights</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {insights.map((insight) => (
            <div key={insight.id} className="flex items-start gap-3 p-3 bg-brand-secondary/50 rounded-lg">
              <div className={`w-8 h-8 flex-shrink-0 bg-white rounded-full flex items-center justify-center ${getTypeColor(insight.type)}`}>
                <i className={`${insight.icon} text-sm`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className="font-medium text-sm text-brand-text-primary">{insight.title}</h4>
                  <Badge variant="secondary" className={`${getImpactColor(insight.impact)} text-xs`}>
                    {insight.impact}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mb-1">{insight.description}</p>
                <p className="text-xs font-medium text-brand-primary">{insight.mandate}</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};